import React, {
  useState,
  useImperativeHandle,
  forwardRef,
  useEffect,
} from "react";
import LoadingSpinner from "../LoadingSpinner";
import Input from "../input/Input";
import SearchDropdown from "../input/SearchDropdown";
import { Label, Icon } from "semantic-ui-react";
import {
  getBulkDropdownData,
  getDropdownData,
  getOtherRefSearchDropdown,
} from "../../api/keyvalue";
import { MODULES } from "../../const";
import Dropdown from "../input/Dropdown";
import { useSelector } from "react-redux";
import InfoList from "../InfoList";
import { User } from "react-feather";
import { formatFromJsonDate } from "../../dateConfig";

const StakeholderUserConfig = forwardRef(
  ({ isLoading, editMode, currentValues, validationErrors }, ref) => {
    const user = useSelector((state) => state.user);
    const [currentErrors, setCurrentErrors] = useState({});
    const [groupName, setGroupName] = useState("");
    const [customer, setCustomer] = useState("");
    const [customerList, setCustomerList] = useState({});
    const [ucgStatus, setUcgStatus] = useState(2);
    const [ucgActive, setUcgActive] = useState(1);
    const [customerResetField, setCustomerResetField] = useState(false);

    const [statusOpt, setStatusOpt] = useState("");
    const [activeOpt, setActiveOpt] = useState("");
    const [auditInfo, setAuditInfo] = useState({});
    const [initialActive, setInitialActive] = useState(
      editMode ? currentValues?.ucgActive : 1,
    );

    // Used by parent form submit
    useImperativeHandle(ref, () => ({
      getData: () => ({
        groupName,
        customers: Object.keys(customerList),
        ucgStatus,
        ucgActive,
      }),
      isDeletedActive: () => ucgActive === 2 && initialActive !== 2,
      resetForm: () => {
        setGroupName("");
        setCustomer("");
        setCustomerList({});
        setCustomerResetField(true);
      },
      setValidationErrors: (errors) => {
        setCurrentErrors(errors);
      },

      // ✅ parent can call this before submit to block on duplicate
      hasDuplicateError: () => !!currentErrors.groupName,
    }));

    const userData = useSelector((state) => state.user.userDetails);
    useEffect(() => {
      (async () => {
        //new
        const { status, active } = await getBulkDropdownData([
          { key: "status" },
          { key: "active" },
        ]);
        setStatusOpt(status);
        setActiveOpt(active);
      })();
    }, []);

    useEffect(() => {
      if (editMode && currentValues) {
        setGroupName(currentValues.ucgName || "");
        setUcgStatus(currentValues.ucgStatus || 2);
        setUcgActive(currentValues.ucgActive || 1);
        const initialCustomers = currentValues.customers || [];
        // Assuming customers is an array of { id, name } or similar
        const detailList = currentValues.userCustomerGroupDetailResponses || [];
        setAuditInfo({
          createdDate: currentValues.createdDate,
          createdBy: currentValues.createdBy,
          lastModifiedBy: currentValues.lastModifiedBy,
          lastModifiedDate: currentValues.lastModifiedDate,
        });
        const mapped = {};
        detailList.forEach((detail) => {
          if (detail.ugdUscId && detail.customerName) {
            mapped[detail.ugdUscId] = detail.customerName;
          }
        });
        setCustomerList(mapped);
      }
    }, [editMode, currentValues]);

    const validate = () => {
      const errors = {};

      if (!groupName) errors.groupName = "Group Name is required";
      if (Object.keys(customerList).length === 0)
        errors.customers = "At least one customer must be added";

      return errors;
    };

    const handleAddCustomer = (data) => {
      if (!customerList[data.key]) {
        setCustomerList((prev) => ({
          ...prev,
          [data.key]: data.title,
        }));
        setCustomer("");
      }
    };

    const handleDeleteCustomer = (key) => {
      setCustomerList((prev) => {
        const updated = { ...prev };
        delete updated[key];
        return updated;
      });
    };

    return (
      <>
        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <form
            className="ui form"
            style={{ overflowY: "auto", height: "75vh" }}
          >
            <div className="ui segment">
              <div className="ui dividing header">
                Stakeholder / User Config
              </div>

              <div className="field">
                <Input
                  required={true}
                  label={"Group Name"}
                  value={groupName}
                  duplicate={false}
                  activeCol="ucg_active"
                  companyCol="ucg_company"
                  companyWise={true}
                  tableName={"authentication.tms_a_user_customer_group"}
                  primaryKey={"ucg_id"}
                  searchField={"ucg_name"}
                  duplicateErrorMessage={"Duplicate User Customer Group Name"}
                  keyType="S"
                  onChange={(e) => setGroupName(e.target.value)}
                  error={currentErrors.groupName}
                  setErrors={setCurrentErrors}
                  errorField={"groupName"}
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { groupName: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                />
              </div>

              {userData.type === 1 ? (
                <div className="field">
                  <SearchDropdown
                    required={true}
                    hideMultiValueList={true}
                    label={"Customer"}
                    reset={customerResetField}
                    setReset={setCustomerResetField}
                    returnFullObject={true}
                    multiValueTwoWayBinding={true}
                    multiple={true}
                    occupySpace={false}
                    onResultSelect={handleAddCustomer}
                    clearable={true}
                    onClear={() => {
                      setCustomer("");
                    }}
                    api={getOtherRefSearchDropdown}
                    apiDependency={{
                      type: "stakeholder_type",
                      value: "Customer",
                    }}
                    error={currentErrors.customers}
                    resetError={() =>
                      setCurrentErrors((prevState) => {
                        const { customers: _, ...rest } = prevState;
                        return rest;
                      })
                    }
                  />
                </div>
              ) : (
                <div className="field">
                  <SearchDropdown
                    required={true}
                    hideMultiValueList={true}
                    label={"Customer"}
                    reset={customerResetField}
                    setReset={setCustomerResetField}
                    returnFullObject={true}
                    multiValueTwoWayBinding={true}
                    multiple={true}
                    occupySpace={false}
                    onResultSelect={handleAddCustomer}
                    clearable={true}
                    onClear={() => {
                      setCustomer("");
                    }}
                    api={getOtherRefSearchDropdown}
                    apiDependency={{
                      type: "user_stakeholder",
                      value: "Requesting Customer",
                      param2: user.username,
                    }}
                    error={currentErrors.customers}
                    resetError={() =>
                      setCurrentErrors((prevState) => {
                        const { customers: _, ...rest } = prevState;
                        return rest;
                      })
                    }
                  />
                </div>
              )}

              <div
                className="ui padded segment"
                style={{ width: "100%", height: "100px", overflow: "auto" }}
              >
                {Object.entries(customerList).length === 0 ? (
                  <div
                    className="ui grey header"
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    No customers added.
                  </div>
                ) : (
                  Object.entries(customerList).map(([key, value]) => (
                    <Label key={key} style={{ marginBottom: "10px" }}>
                      {value}
                      <Icon
                        name="delete"
                        onClick={() => handleDeleteCustomer(key)}
                      />
                    </Label>
                  ))
                )}
              </div>
              <div className="two fields">
                <Dropdown
                  editMode={editMode}
                  field="ucg_status"
                  label="Status"
                  clearable={false}
                  required={true}
                  placeholder="Select Status"
                  options={statusOpt}
                  value={ucgStatus}
                  onChange={(e, data) => setUcgStatus(data.value)}
                  error={currentErrors.ucgStatus}
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { ucgStatus: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                />

                <Dropdown
                  editMode={editMode}
                  field="ucg_active"
                  label="Active"
                  clearable={false}
                  required={true}
                  placeholder="Select Active"
                  options={activeOpt}
                  value={ucgActive}
                  onChange={(e, data) => setUcgActive(data.value)}
                  error={currentErrors.ucgActive}
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { ucgActive: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                />
              </div>
            </div>
            {editMode && (
              <InfoList
                title="User Information"
                content={[
                  {
                    title: "Created By",
                    value: auditInfo.createdBy ? auditInfo.createdBy : "N/A",
                  },
                  {
                    title: "Created Date",
                    value: auditInfo.createdDate
                      ? formatFromJsonDate(auditInfo.createdDate)
                      : "N/A",
                  },
                  {
                    title: "Last Modified By",
                    value: auditInfo.lastModifiedBy
                      ? auditInfo.lastModifiedBy
                      : "N/A",
                  },
                  {
                    title: "Last Modified Date",
                    value: auditInfo.lastModifiedDate
                      ? formatFromJsonDate(auditInfo.lastModifiedDate)
                      : "N/A",
                  },
                ]}
                Icon={User}
              />
            )}
          </form>
        )}
      </>
    );
  },
);

export default StakeholderUserConfig;
